import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Loader2, Trash2, Save, Sparkles, X } from "lucide-react";

const TYPE_STYLE = {
  facto: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
  decisao: "bg-amber-500/10 text-amber-400 border-amber-500/20",
  preferencia: "bg-indigo-500/10 text-indigo-400 border-indigo-500/20",
  tarefa: "bg-purple-500/10 text-purple-400 border-purple-500/20",
  aprendizagem: "bg-sky-500/10 text-sky-400 border-sky-500/20",
};

export default function ReviewDialog({ open, onOpenChange, extracting, draft, onSave, saving }) {
  const [titulo, setTitulo] = useState("");
  const [resumo, setResumo] = useState("");
  const [factos, setFactos] = useState([]);
  const [entidades, setEntidades] = useState([]);

  useEffect(() => {
    if (draft) {
      setTitulo(draft.titulo || "");
      setResumo(draft.resumo || "");
      setFactos((draft.factos || []).map((f, i) => ({ ...f, _k: i })));
      setEntidades(draft.entidades || []);
    }
  }, [draft]);

  const removeFacto = (k) => setFactos((fs) => fs.filter((f) => f._k !== k));
  const removeEntidade = (i) => setEntidades((es) => es.filter((_, idx) => idx !== i));

  const save = () => {
    onSave({
      titulo,
      resumo,
      factos: factos.map(({ texto, tipo }) => ({ texto, tipo })),
      entidades,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="review-dialog"
        className="bg-slate-950 border-slate-800 text-slate-100 max-w-lg max-h-[85vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display text-emerald-400">
            <Sparkles size={18} /> Revisão dos factos
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            Nada entra na memória sem passar por aqui. Apaga o que não presta.
          </DialogDescription>
        </DialogHeader>

        {extracting ? (
          <div className="py-12 flex flex-col items-center gap-3 text-slate-400">
            <Loader2 size={26} className="animate-spin text-emerald-400" />
            <span className="text-sm font-mono">a extrair factos da conversa…</span>
          </div>
        ) : (
          <div className="space-y-4 mt-2">
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Título</label>
              <input
                data-testid="review-titulo"
                value={titulo}
                onChange={(e) => setTitulo(e.target.value)}
                className="w-full px-3 py-2 rounded-lg bg-slate-900/70 border border-slate-800 focus:border-emerald-500/60 focus:outline-none text-sm"
              />
            </div>
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Resumo</label>
              <textarea
                data-testid="review-resumo"
                value={resumo}
                onChange={(e) => setResumo(e.target.value)}
                rows={2}
                className="w-full px-3 py-2 rounded-lg bg-slate-900/70 border border-slate-800 focus:border-emerald-500/60 focus:outline-none text-sm resize-none"
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-xs text-slate-400">
                  Factos ({factos.length})
                </label>
              </div>
              <div className="space-y-2">
                {factos.length === 0 && (
                  <p className="text-sm text-slate-500 py-4 text-center border border-dashed border-slate-800 rounded-lg">
                    Sem factos dignos de memória neste excerto.
                  </p>
                )}
                {factos.map((f) => (
                  <div
                    key={f._k}
                    data-testid="review-fact-item"
                    className="group flex items-start gap-2 p-3 rounded-lg bg-slate-900/60 border border-slate-800"
                  >
                    <span
                      className={`shrink-0 mt-0.5 text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-full border ${
                        TYPE_STYLE[f.tipo] || TYPE_STYLE.facto
                      }`}
                    >
                      {f.tipo}
                    </span>
                    <p className="flex-1 text-sm text-slate-200 leading-relaxed">{f.texto}</p>
                    <button
                      data-testid="review-fact-delete"
                      onClick={() => removeFacto(f._k)}
                      className="text-slate-500 hover:text-red-400 transition-colors"
                    >
                      <Trash2 size={15} />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {entidades.length > 0 && (
              <div>
                <label className="text-xs text-slate-400 mb-1.5 block">Entidades</label>
                <div className="flex flex-wrap gap-2">
                  {entidades.map((e, i) => (
                    <span
                      key={i}
                      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-slate-800/70 border border-slate-700 text-xs text-slate-300"
                    >
                      {e.nome}
                      <span className="text-slate-500">·{e.tipo}</span>
                      <button onClick={() => removeEntidade(i)} className="hover:text-red-400">
                        <X size={11} />
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <button
            onClick={() => onOpenChange(false)}
            className="px-4 py-2 rounded-lg border border-slate-700 text-slate-300 text-sm hover:bg-slate-800/60 transition-colors"
          >
            Cancelar
          </button>
          <button
            data-testid="review-save-button"
            onClick={save}
            disabled={extracting || saving || factos.length === 0}
            className="px-4 py-2 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold text-sm flex items-center gap-2 disabled:opacity-50"
          >
            {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Guardar na
            memória
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
